<template>
    <div
        v-if="layout"
        class="container mx-auto">
        <AtomTitle level="2">
            Товар входит в состав {{ prefix }}
            <NuxtLink
                :to="system?.link"
                :class="
                    system?.link
                        ? 'underline decoration-2 underline-offset-8 hover:text-white/60'
                        : ''
                "
                class="text-24/133 transition-colors duration-200 lg:text-48/100">
                {{
                    initialData?.data.page_title || modelRange?.model_range_name
                }}
            </NuxtLink>
        </AtomTitle>

        <div
            :class="
                !layout.bottom && !layout.right
                    ? 'lg:max-w-[44rem] xl:max-w-[54rem]'
                    : ''
            "
            class="mt-5 w-full rounded-[1.75rem] bg-white/20 p-2 lg:mt-10">
            <div
                :class="[
                    layout.bottom ? 'items-center' : 'items-stretch',
                    !layout.bottom && !layout.right
                        ? 'lg:grid-cols-[1fr_auto_1fr_auto]'
                        : 'lg:grid-cols-[1fr_auto_1fr_auto_1fr]',
                ]"
                class="grid grid-cols-1">
                <div class="flex items-stretch">
                    <BaseDiagramBlock
                        :group-name="layout.left.name"
                        :products="layout.left.products?.data || []"
                        :current-product-slug="currentProductSlug"
                        class="h-full" />
                </div>

                <div
                    class="flex items-center justify-center max-lg:rotate-90 lg:px-0">
                    <BaseDiagramConnector :type="layout.conn1" />
                </div>

                <div class="flex items-stretch">
                    <BaseDiagramBlock
                        :group-name="layout.center.name"
                        :products="layout.center.products?.data || []"
                        :current-product-slug="currentProductSlug"
                        class="h-full" />
                </div>

                <div
                    v-if="layout.right"
                    class="flex items-center justify-center max-lg:rotate-90 lg:px-0">
                    <BaseDiagramConnector :type="layout.conn2" />
                </div>

                <div
                    v-if="layout.right"
                    class="flex items-stretch">
                    <BaseDiagramBlock
                        :group-name="layout.right.name"
                        :products="layout.right.products?.data || []"
                        :current-product-slug="currentProductSlug"
                        class="h-full" />
                </div>
            </div>

            <template v-if="layout.bottom">
                <div class="flex flex-col justify-center lg:col-span-5">
                    <div class="flex justify-center lg:col-start-3">
                        <BaseDiagramConnector
                            :type="layout.conn3"
                            class="-rotate-90 lg:-rotate-90" />
                    </div>

                    <div class="lg:col-span-5">
                        <BaseDiagramBlock
                            text-center
                            :group-name="layout.bottom.name"
                            :products="layout.bottom.products?.data || []"
                            :current-product-slug="currentProductSlug" />
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { Category } from '~/types/global.types'
import type { ProductLinePage } from '~/types/pages.types'

import AtomTitle from '~/components/atom/atom-title/AtomTitle.vue'
import BaseDiagramBlock from '~/components/base/base-diagram-block/BaseDiagramBlock.vue'
import BaseDiagramConnector from '~/components/base/base-diagram-connector/BaseDiagramConnector.vue'

const props = defineProps<{
    initialData?: any
    currentProductSlug: string
    category: Category
    modelRange?: ProductLinePage
}>()

interface DiagramRelation {
    position?: number
    type?: string
    parent?: {
        data?: {
            id?: number | string
            name: string
            products?: ProductLinePage['products']
        }
    }
    children?: {
        data?: {
            id?: number | string
            name: string
            products?: ProductLinePage['products']
        }
    }
}

type DiagramNode = NonNullable<DiagramRelation['parent']>['data']

interface DiagramLayout {
    left: NonNullable<DiagramNode>
    center: NonNullable<DiagramNode>
    right: DiagramNode | null
    bottom?: DiagramNode
    conn1: string
    conn2: string
    conn3: string
}

const systems = [
    // radioguides
    {
        slug: 'reinvox-duo-sale',
        title: 'радиогидов',
        // modelRangeName: 'Reinvox DUO',
    },
    {
        slug: 'reinvox-pro',
        title: 'радиогидов',
    },
    // audioguides
    {
        slug: '',
        parentSlug: 'audioguides',
        title: 'аудиогидов',
    },
    {
        slug: 'manual',
        title: 'аудиогидов',
    },
    {
        slug: 'auto',
        title: 'аудиогидов',
    },
    // cabins
    {
        slug: 'iso',
        title: 'кабин переводчиков',
    },
    {
        slug: 'tabletop',
        title: 'кабин переводчиков',
    },
    // conference-systems
    {
        slug: 'reinvox-concept',
        title: 'конференц-систем',
    },
    {
        slug: 'reinvox-elegant',
        title: 'конференц-систем',
    },
    // interpretation (sinhroperevod)
    {
        slug: 'consoles',
        title: 'систем синхроперевода',
    },
    {
        slug: 'transmission',
        title: 'систем синхроперевода',
    },
    // dostupnaia-sreda (dostupsreda)
    {
        slug: 'tiflo',
        title: 'оборудования для тифлокомментирования',
    },
    {
        slug: 'radioclass',
        title: 'радиоклассов и FM-систем',
    },
]

const system = computed(() => {
    const result = systems.filter((system) => {
        return system.slug === props?.modelRange?.slug
    })

    let link = ''
    if (props.category.page.parent_slug && props.category.page.slug) {
        link = `/${props.category.page.parent_slug}/${props.category.page.slug}`
    } else if (props.initialData?.data?.link) {
        link = `${props.initialData?.data?.link}`
    }

    return {
        ...result[0],
        link: link,
    }
})

const prefix = computed(() => {
    return props.modelRange?.model_range_name ? system.value.title : ''
})

const layout = computed<DiagramLayout | null>(() => {
    const relations: DiagramRelation[] =
        props.initialData?.data?.diagramNodeRelation?.data || []

    if (!relations.length) return null

    // 1. Берем связи. Если по position не нашли, берем по индексу в массиве
    const rel1 = relations.find((r) => r.position === 1) || relations[0]
    const rel2 = relations.find((r) => r.position === 2) || relations[1]
    const rel3 = relations.find((r) => r.position === 3) || relations[2]

    const left = rel1.parent?.data
    const center = rel1.children?.data

    if (!left || !center) {
        return null
    }

    let right: DiagramNode | null = null

    if (rel2) {
        const r2Parent = rel2.parent?.data
        const r2Child = rel2.children?.data

        // [Аксессуары] -> [Для тифло] -> [Для слушателей]
        // Если родитель второй связи — это центр первой
        if (r2Parent?.id === center?.id) {
            right = r2Child
        }
        // [Передатчик] -> [Приемник] <- [Зарядка]
        // Если ребенок второй связи — это центр первой
        else if (r2Child?.id === center?.id) {
            right = r2Parent
        }
        // [Центр] -> [Лево] И [Центр] -> [Право]
        else if (r2Parent?.id === left?.id) {
            right = r2Child
        }
    }

    return {
        left,
        center,
        right,
        bottom: rel3?.parent?.data,
        conn1: rel1?.type || 'Стрелка',
        conn2: rel2?.type || 'Стрелка',
        conn3: rel3?.type || 'Стрелка',
    }
})
</script>
